import {AxiosHeaders} from "axios";
import {httpClient} from "./httpClient";

const STORAGE_KEY = "liquid-panel.auth";
const EXPIRY_BUFFER_MS = 15 * 1000;

export type LoginPayload = {
    login: string;
    password: string;
};

type ApiAuthResponse = {
    access_token: string;
    access_token_expires_at: string;
    refresh_token: string;
    refresh_token_expires_at: string;
};

export type AuthTokens = {
    accessToken: string;
    accessTokenExpiresAt: string;
    refreshToken: string;
    refreshTokenExpiresAt: string;
};

const isBrowser = typeof window !== "undefined";

const normalizeTokens = (payload: ApiAuthResponse): AuthTokens => ({
    accessToken: payload.access_token,
    accessTokenExpiresAt: payload.access_token_expires_at,
    refreshToken: payload.refresh_token,
    refreshTokenExpiresAt: payload.refresh_token_expires_at,
});

const isExpired = (isoDate: string) => {
    const target = Date.parse(isoDate);
    if (Number.isNaN(target)) {
        return true;
    }
    return target - EXPIRY_BUFFER_MS <= Date.now();
};

const readTokens = (): AuthTokens | null => {
    if (!isBrowser) {
        return null;
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
        return null;
    }
    try {
        return JSON.parse(raw) as AuthTokens;
    } catch {
        return null;
    }
};

const writeTokens = (tokens: AuthTokens) => {
    if (!isBrowser) {
        return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
};

const clearTokens = () => {
    if (!isBrowser) {
        return;
    }
    localStorage.removeItem(STORAGE_KEY);
};

export const authStorage = {
    get: readTokens,
    set: writeTokens,
    clear: clearTokens,
};

export async function login(payload: LoginPayload): Promise<AuthTokens> {
    const {data} = await httpClient.post<ApiAuthResponse>("/users/login", payload);
    const normalized = normalizeTokens(data);
    authStorage.set(normalized);
    return normalized;
}

export async function refreshTokens(
    refreshToken?: string
): Promise<AuthTokens> {
    const tokenToUse = refreshToken ?? authStorage.get()?.refreshToken;
    if (!tokenToUse) {
        throw new Error("Missing refresh token");
    }
    const {data} = await httpClient.post<ApiAuthResponse>(
        "/users/refresh",
        {refresh_token: tokenToUse}
    );
    const normalized = normalizeTokens(data);
    authStorage.set(normalized);
    return normalized;
}

export async function ensureValidSession(): Promise<AuthTokens> {
    const tokens = authStorage.get();
    if (!tokens) {
        throw new Error("Missing tokens");
    }

    if (isExpired(tokens.accessTokenExpiresAt)) {
        if (isExpired(tokens.refreshTokenExpiresAt)) {
            throw new Error("Session expired");
        }
        return refreshTokens(tokens.refreshToken);
    }

    return tokens;
}

export function logout() {
    authStorage.clear();
}

export function getAccessToken(): string | null {
    return authStorage.get()?.accessToken ?? null;
}

// Флаг для предотвращения бесконечных циклов при обновлении токенов
let isRefreshing = false;
let isRedirecting = false; // Флаг для предотвращения множественных редиректов
let failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (error?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

httpClient.interceptors.request.use((config) => {
    // Пропускаем добавление токена для эндпоинтов авторизации
    if (config.url?.includes('/users/login') || config.url?.includes('/users/refresh')) {
        return config;
    }

    // Добавляем токен если он есть (даже если истек)
    // Response interceptor обработает 401 и автоматически обновит токены
    const accessToken = getAccessToken();
    if (!accessToken) {
        return config;
    }

    if (config.headers instanceof AxiosHeaders) {
        config.headers.set("Authorization", `Bearer ${accessToken}`);
        return config;
    }

    const headers = AxiosHeaders.from(config.headers ?? undefined);
    headers.set("Authorization", `Bearer ${accessToken}`);
    config.headers = headers;
    return config;
});

httpClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Проверяем что есть оригинальный запрос
        if (!originalRequest) {
            return Promise.reject(error);
        }

        // Пропускаем повторные попытки и эндпоинты авторизации
        const isAuthEndpoint = originalRequest.url?.includes('/users/login') || originalRequest.url?.includes('/users/refresh');
        if (originalRequest._retry || isAuthEndpoint) {
            return Promise.reject(error);
        }

        // Обрабатываем только 401 ошибки
        if (error.response?.status === 401) {
            if (isRefreshing) {
                // Если уже идет обновление, добавляем запрос в очередь
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(() => {
                        const newToken = getAccessToken();
                        if (newToken) {
                            originalRequest.headers = originalRequest.headers || {};
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        }
                        return httpClient(originalRequest);
                    })
                    .catch((err) => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const tokens = authStorage.get();
                if (!tokens) {
                    throw new Error("No tokens");
                }

                // Пытаемся обновить токены
                const newTokens = await refreshTokens(tokens.refreshToken);

                // Обновляем заголовок с новым токеном
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;

                // Обрабатываем очередь ожидающих запросов (должно быть до сброса флага)
                processQueue(null, newTokens.accessToken);

                // Сбрасываем флаг только после успешного обновления и обработки очереди
                isRefreshing = false;

                // Повторяем оригинальный запрос с новым токеном
                return httpClient(originalRequest);
            } catch (refreshError) {
                // Если обновление не удалось, очищаем токены и отклоняем все запросы
                processQueue(refreshError as Error, null);
                authStorage.clear();
                isRefreshing = false;

                // Перенаправляем на логин только если мы не на странице логина и еще не редиректим
                if (!isRedirecting && window.location.pathname !== '/login') {
                    isRedirecting = true;
                    window.location.href = '/login';
                }

                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

